/* The inbound lists that need a person but have no hold row: documents stopped after routing
   (shown on the Held tab beside the unrouted holds) and documents rejected outright.

   <HeldPanel holds={heldRows} exceptions={stopped} isLoading={false} onAssign={open} />
   <RejectedPanel rows={rejected} isLoading={false} /> */

import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { CompanyChip, Notice } from "../../design/components";
import { dateTime, omr } from "../../lib/format";
import type { InboundDocumentRow } from "../../api/inbound";
import type { HeldRow } from "../../api/routing";
import { HeldTable } from "./HeldTable";
import styles from "./inbound.module.css";

export function IssuesTable({ rows, label }: { rows: InboundDocumentRow[]; label: string }) {
  const { t } = useTranslation();
  return (
    <div className="table-wrap">
      <table className="tbl" aria-label={label}>
        <thead>
          <tr>
            <th className="nowrap">{t("inbound.issues.colReceived", "Received")}</th>
            <th>{t("inbound.issues.colSupplier", "Supplier")}</th>
            <th>{t("inbound.issues.colInvoiceNumber", "Invoice number")}</th>
            <th>{t("inbound.issues.colCompany", "Company")}</th>
            <th className="r">{t("inbound.issues.colTotal", "Total")}</th>
            <th>{t("inbound.issues.colReason", "Reason")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="nowrap">{dateTime(row.createdAt)}</td>
              <td>{row.counterparty ?? "—"}</td>
              <td>
                <Link className="link" to={`/inbound/${row.id}`}>
                  {row.invoiceNumber ?? row.id}
                </Link>
              </td>
              <td>
                {row.entityId ? (
                  <CompanyChip entityId={row.entityId} name={row.entityName ?? ""} code={row.entityCode ?? ""} />
                ) : (
                  "—"
                )}
              </td>
              <td className="r nowrap">
                {omr(row.totalAmount)}
                {row.currency ? ` ${row.currency}` : ""}
              </td>
              <td className="mono nowrap">{row.errorCode ?? row.stage}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function HeldPanel({
  holds,
  exceptions,
  isLoading,
  onAssign,
}: {
  holds: HeldRow[];
  exceptions: InboundDocumentRow[];
  isLoading: boolean;
  onAssign: (row: HeldRow) => void;
}) {
  const { t } = useTranslation();
  if (!holds.length && !exceptions.length && !isLoading) {
    return (
      <div className="card-body">
        <Notice kind="info">
          {t("inbound.issues.heldEmpty", "Nothing held. Every received document has routed to a company.")}
        </Notice>
      </div>
    );
  }
  return (
    <>
      {holds.length ? <HeldTable rows={holds} isLoading={isLoading} onAssign={onAssign} /> : null}
      {exceptions.length ? (
        <>
          <p className={styles.sectionNote}>
            {t(
              "inbound.issues.stoppedNote",
              "Routed to a company, then stopped before landing. Open one to retry or reject it.",
            )}
          </p>
          <IssuesTable rows={exceptions} label={t("inbound.issues.stoppedLabel", "Stopped after routing")} />
        </>
      ) : null}
    </>
  );
}

export function RejectedPanel({ rows, isLoading }: { rows: InboundDocumentRow[]; isLoading: boolean }) {
  const { t } = useTranslation();
  if (!rows.length && !isLoading) {
    return (
      <div className="card-body">
        <Notice kind="info">{t("inbound.issues.rejectedEmpty", "Nothing rejected.")}</Notice>
      </div>
    );
  }
  return <IssuesTable rows={rows} label={t("inbound.issues.rejectedLabel", "Rejected")} />;
}
